import { MouseEvent } from 'react';
import { GridColDef, GridRenderCellParams, GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { GridValueFormatterParams } from '@mui/x-data-grid/models/params/gridCellParams';

import { deCamelize, formatNumberValue, formatPhoneNumber, formatZipcode } from '../../commonFunctions';
import { statesList } from '../../constants/statesList';
import { GridColZipCode } from './GridColZipCode';
import { GridColSelect } from './GridColSelect';
import { GridColPhone } from './GridColPhone';
import { GridColNumber } from './GridColNumber';
import { GridCell } from './GridCell';

export const renderStateCell = (params: GridRenderEditCellParams) => (
  <GridColSelect isSearchable params={params} options={statesList} />
);

export const renderBooleanCell = ({ value }: GridRenderCellParams) => (value ? 'Yes' : 'No');

export const gridBooleanCell = {
  type: 'boolean',
  editable: true,
  onClick: (event: MouseEvent) => event.stopPropagation(),
  renderCell: renderBooleanCell,
};

export const gridStateCell = {
  editable: true,
  flex: 1,
  minWidth: 160,
  renderEditCell: renderStateCell,
};

export const gridZipCell = {
  editable: true,
  minWidth: 120,
  renderEditCell: (params: GridRenderEditCellParams) => <GridColZipCode params={params} />,
  valueFormatter: ({ value }: GridValueFormatterParams) => formatZipcode(value),
};


export const gridPhoneCell = {
  renderEditCell: (params: GridRenderEditCellParams) => <GridColPhone params={params} />,
  valueFormatter: ({ value }: GridValueFormatterParams) => formatPhoneNumber(value),
};

const generateGridNumberCell = (prefix?: string, suffix?: string, nullable?: boolean) => ({
  renderEditCell: (params: GridRenderEditCellParams) => <GridColNumber params={params} />,
  valueFormatter: ({ value }: GridValueFormatterParams) =>
    formatNumberValue(prefix, suffix, nullable)({ value } as GridValueFormatterParams),
});

export const gridNumberCell = generateGridNumberCell();
export const gridCurrencyCell = generateGridNumberCell('$');
export const gridCurrencyCellNullable = generateGridNumberCell('$', undefined, true);
export const gridPercentageCell = generateGridNumberCell('', '%');
export const gridPercentageCellNullable = generateGridNumberCell('', '%', true);

interface IGridColDefExtended extends GridColDef {
  required?: boolean;
  textWrap?: boolean
}

type TDataGridRowOptions = Omit<IGridColDefExtended, 'field'>;

export const dataGridCol = (field: string, options?: TDataGridRowOptions): GridColDef & { minHeight: number } => {
  const headerName = deCamelize(field);
  return {
    field,
    headerName,
    description: headerName,
    minHeight: 55,
    maxWidth: 1000,
    renderCell: (props) => options?.textWrap ? props.formattedValue || props.value || '' : <GridCell text={props.formattedValue || props.value || ''} />,
    ...options,
  };
};
